import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  RefreshCw, KeyRound, Eye, Copy, Check, Truck, Store, Info,
  Plus, Power, PowerOff, Trash2, Pencil,
} from "lucide-react";

// CS 20.8 — Per-recipient Distribution Links admin page.
// Each link_type ("distributor" | "retailer") can have multiple NAMED
// recipients; each recipient gets its own URL, Owner PIN, and Staff PIN.
export default function DistributionLinks() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/distribution-recipients").then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Failed to load distribution recipients"));
  }, []);
  useEffect(() => { if (canEdit) load(); }, [load, canEdit]);

  if (!canEdit) {
    return (
      <div className="luxe-card p-6 bg-amber-50 border-amber-200 text-amber-800 text-sm" data-testid="dist-links-restricted">
        Only Master Admin can manage Distribution links.
      </div>
    );
  }
  if (!items) return <div className="space-y-4"><div className="skeleton h-12 w-64" /><div className="skeleton h-64" /></div>;

  const distributors = items.filter((x) => x.link_type === "distributor");
  const retailers    = items.filter((x) => x.link_type === "retailer");

  const create = async (link_type, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error("Recipient name is required"); return; }
    setBusy(true);
    try {
      await api.post("/distribution-recipients", { link_type, name: trimmed });
      toast.success(`${trimmed} added`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="distribution-links-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A84C] mb-1">Distribution Module</p>
        <h1 className="font-serif text-4xl text-[#0D1B2A]">Distribution Links</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Create a NAMED recipient under each link type. Every recipient gets a unique URL with
          its own Owner PIN (full visibility) and Staff PIN (origin-cost hidden).
        </p>
      </header>

      <div className="luxe-card p-4 bg-blue-50 border-blue-200 text-xs text-blue-800 flex gap-2 items-start">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <strong>Owner / Staff PIN model:</strong> Distributor link → Owner sees Distributor + Retailer + Customer MRP. Staff sees Retailer + Customer MRP only.
          Retailer link → Owner sees Retailer + Customer MRP. Staff sees Customer MRP only. The origin cost is stripped at the API layer when staff PIN is used — never trust UI hiding.
        </div>
      </div>

      <Section
        icon={Truck} accent="border-l-[#0D1B2A]" title="Distributor Recipients"
        note="Each row is one Distributor relationship — its own URL + PINs"
        items={distributors} kind="distributor" busy={busy} onReload={load}
        onCreate={(name) => create("distributor", name)} testid="distributor-section"
      />
      <Section
        icon={Store} accent="border-l-[#C9A84C]" title="Retailer Recipients"
        note="Each row is one Retailer-level relationship"
        items={retailers} kind="retailer" busy={busy} onReload={load}
        onCreate={(name) => create("retailer", name)} testid="retailer-section"
      />
    </div>
  );
}

function Section({ icon: Icon, accent, title, note, items, kind, busy, onReload, onCreate, testid }) {
  const [newName, setNewName] = useState("");
  const submit = (e) => {
    e.preventDefault();
    onCreate(newName);
    setNewName("");
  };
  return (
    <section className={`luxe-card p-6 border-l-4 ${accent}`} data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded bg-[#0D1B2A]/5"><Icon className="w-5 h-5 text-[#0D1B2A]" /></div>
        <div className="flex-1">
          <h3 className="font-serif text-2xl text-[#0D1B2A]">{title}</h3>
          <p className="text-[11px] text-slate-500">{note}</p>
        </div>
      </div>

      <form className="flex gap-2 items-center pb-4 border-b border-[#0D1B2A]/10 mb-4" onSubmit={submit}>
        <Input
          placeholder={`Add a ${kind} recipient name (e.g. Rajesh Jewellers)`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="max-w-md"
          data-testid={`${testid}-new-name`}
        />
        <Button type="submit" disabled={busy || !newName.trim()} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4 mr-1" /> Add recipient
        </Button>
      </form>

      {items.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No {kind} recipients yet. Add one above to generate a unique link.</p>
      ) : (
        <div className="space-y-4">
          {items.map((rec) => (
            <RecipientCard key={rec.id} rec={rec} kind={kind} onReload={onReload} />
          ))}
        </div>
      )}
    </section>
  );
}

function RecipientCard({ rec, kind, onReload }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const [pinEditor, setPinEditor] = useState(null); // 'owner_pin'|'staff_pin'
  const [pinValue, setPinValue] = useState("");
  const [nameEditing, setNameEditing] = useState(false);
  const [nameValue, setNameValue] = useState(rec.name);

  const base = window.location.origin;
  const url = `${base}/dist/${kind}/${rec.token}`;
  const inactive = !rec.active;

  const copy = (key, txt) => {
    navigator.clipboard?.writeText(txt).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1500);
    });
  };

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      await api.patch(`/distribution-recipients/${rec.id}`, body);
      if (msg) toast.success(msg);
      onReload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const savePin = async () => {
    if (!pinEditor) return;
    if (!/^\d{4}$/.test(pinValue)) return toast.error("PIN must be exactly 4 digits");
    await patch({ [pinEditor]: pinValue }, "PIN updated");
    setPinEditor(null); setPinValue("");
  };

  const saveName = async () => {
    const v = nameValue.trim();
    if (!v) return toast.error("Name is required");
    await patch({ name: v }, "Name updated");
    setNameEditing(false);
  };

  const regen = async () => {
    if (!window.confirm(`Regenerate ${rec.name}'s link? Their old URL stops working immediately.`)) return;
    setBusy(true);
    try {
      await api.post(`/distribution-recipients/${rec.id}/regenerate-token`);
      toast.success("Link regenerated");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${rec.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/distribution-recipients/${rec.id}`);
      toast.success(`${rec.name} removed`);
      onReload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-md border ${inactive ? "border-amber-200 bg-amber-50/40" : "border-[#0D1B2A]/10 bg-white"} p-4`} data-testid={`recipient-${rec.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        {nameEditing ? (
          <>
            <Input value={nameValue} onChange={(e) => setNameValue(e.target.value)} className="max-w-xs h-9" autoFocus data-testid={`recipient-${rec.id}-name-input`} />
            <Button size="sm" onClick={saveName} className="h-9 bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid={`recipient-${rec.id}-name-save`}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setNameEditing(false); setNameValue(rec.name); }} className="h-9">Cancel</Button>
          </>
        ) : (
          <>
            <h4 className="font-serif text-xl text-[#0D1B2A]" data-testid={`recipient-${rec.id}-name`}>{rec.name}</h4>
            <Button size="sm" variant="ghost" onClick={() => setNameEditing(true)} className="h-7 px-2" title="Rename" data-testid={`recipient-${rec.id}-name-edit`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            {inactive && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold">Inactive</span>}
          </>
        )}
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => patch({ active: !rec.active }, rec.active ? "Recipient disabled" : "Recipient enabled")} disabled={busy} className="h-8 px-3 text-xs" title={rec.active ? "Deactivate link" : "Activate link"} data-testid={`recipient-${rec.id}-toggle-active`}>
            {rec.active ? <><PowerOff className="w-3.5 h-3.5 mr-1" /> Deactivate</> : <><Power className="w-3.5 h-3.5 mr-1" /> Activate</>}
          </Button>
          <Button size="sm" variant="outline" onClick={remove} disabled={busy} className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50" title="Delete recipient" data-testid={`recipient-${rec.id}-delete`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </span>
      </div>

      <div className="mt-3">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Public URL</label>
        <div className="flex gap-2">
          <Input value={url} readOnly className="font-mono text-xs" data-testid={`recipient-${rec.id}-url`} />
          <Button variant="outline" onClick={() => copy("url", url)} title="Copy URL" data-testid={`recipient-${rec.id}-copy`}>
            {copied === "url" ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          </Button>
          <Button variant="outline" onClick={() => window.open(url, "_blank")} title="Preview" data-testid={`recipient-${rec.id}-preview`}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={regen} disabled={busy} title="Regenerate link" data-testid={`recipient-${rec.id}-regen`}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-[#0D1B2A]/10 mt-3">
        {["owner_pin", "staff_pin"].map((field) => {
          const editing = pinEditor === field;
          const label = field === "owner_pin" ? "Owner PIN" : "Staff PIN";
          return (
            <div key={field} className="flex items-center gap-2" data-testid={`recipient-${rec.id}-${field}`}>
              <KeyRound className="w-4 h-4 text-[#C9A84C]" />
              <span className="text-xs text-slate-600 min-w-[80px]">{label}</span>
              {editing ? (
                <>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinValue}
                    onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    className="font-mono text-sm h-8 max-w-[100px]"
                    placeholder="4-digit"
                    autoFocus
                    data-testid={`recipient-${rec.id}-${field}-input`}
                  />
                  <Button size="sm" onClick={savePin} className="h-8 px-3 bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid={`recipient-${rec.id}-${field}-save`}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setPinEditor(null); setPinValue(""); }} className="h-8 px-2">Cancel</Button>
                </>
              ) : (
                <>
                  <span className="font-mono text-sm text-slate-400 tracking-widest">••••</span>
                  <Button size="sm" variant="outline" onClick={() => { setPinEditor(field); setPinValue(""); }} className="h-8 px-3 text-xs" data-testid={`recipient-${rec.id}-${field}-edit`}>
                    Change
                  </Button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
